import axios from 'axios';
import {
  Service, ServiceInput, Check, CheckHourly, CheckTypeInfo, Group, ConfigExport, ImportResult, Incident, IncidentSummary,
  AdHocTestResult, FleetReport, Scorecard, HourOfDayRow,
  NotificationChannel, NotificationChannelInput, NotificationDelivery, NotificationType, NotificationTestResult,
  MaintenanceWindow, MaintenanceWindowInput,
  StatusPage, StatusPageDetail, StatusPageInput, StatusPageLayoutInput, StatusPost, InstanceSettings, PublicStatus,
  ApiKey, ApiKeyCreated, ApiKeyInput, BulkInput, BulkResult, ServiceTemplate, BackupStatus, BackupResult, RetentionPolicy,
  ConfigFileState, PushSubscriptionInfo,
} from '../types';

const api = axios.create({
  baseURL: '/api/v1',
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

// 401 interceptor: attempt token refresh, then retry
let isRefreshing = false;
let failedQueue: Array<{ resolve: (v: unknown) => void; reject: (e: unknown) => void }> = [];

function processQueue(error: unknown) {
  failedQueue.forEach(p => {
    if (error) p.reject(error);
    else p.resolve(undefined);
  });
  failedQueue = [];
}

api.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => api(originalRequest));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        await axios.post('/api/v2/auth/refresh', {}, { withCredentials: true });
        processQueue(null);
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

// Services
export const getServices = (params?: Record<string, string>) =>
  api.get<Service[]>('/services', { params }).then(r => r.data);

export const getService = (id: number) =>
  api.get<Service>(`/services/${id}`).then(r => r.data);

export const createService = (data: ServiceInput) =>
  api.post<Service>('/services', data).then(r => r.data);

export const updateService = (id: number, data: ServiceInput) =>
  api.put<Service>(`/services/${id}`, data).then(r => r.data);

export const deleteService = (id: number) =>
  api.delete(`/services/${id}`);

export const testService = (id: number) =>
  api.post(`/services/${id}/test`).then(r => r.data);

export const triggerCheck = (id: number) =>
  api.post(`/services/${id}/checks`).then(r => r.data);

export const getChecks = (id: number, limit = 100, offset = 0, hours?: number) =>
  api.get<{ data: Check[]; total: number }>(`/services/${id}/checks`, {
    params: { limit, offset, ...(hours !== undefined ? { hours } : {}) },
  }).then(r => r.data);

/** Try a configuration without saving it — any type. */
export const testServiceConfig = (data: ServiceInput) =>
  api.post<AdHocTestResult>('/services/test', data).then(r => r.data);

export const getServiceTypes = () =>
  api.get<CheckTypeInfo[]>('/services/types').then(r => r.data);

/** Heartbeat services: issue a new push token; the old URL stops working at once. */
export const rotatePushToken = (id: number) =>
  api.post<Service>(`/services/${id}/push-token`).then(r => r.data);

export const getHourlyStats = (id: number, hours = 168) =>
  api.get<CheckHourly[]>(`/services/${id}/checks/hourly`, { params: { hours } }).then(r => r.data);

/** One action over many services — one request, one audit entry. */
export const bulkServices = (data: BulkInput) =>
  api.post<BulkResult>('/services/bulk', data).then(r => r.data);

/** A new service with the same configuration (credentials included, server-side) and groups. */
export const cloneService = (id: number, data: { name?: string; enabled?: boolean } = {}) =>
  api.post<Service>(`/services/${id}/clone`, data).then(r => r.data);

/** Plain href for the raw-check download — cookie auth travels with the request. */
export const checksExportUrl = (id: number, params: { from?: string; to?: string; format?: 'csv' | 'json' }) => {
  const q = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined)) as Record<string, string>);
  return `/api/v1/services/${id}/checks/export?${q.toString()}`;
};

// Service templates
export const getServiceTemplates = () =>
  api.get<ServiceTemplate[]>('/services/templates').then(r => r.data);

export const createServiceTemplate = (data: { name: string; description?: string | null; payload?: Partial<ServiceInput>; from_service_id?: number }) =>
  api.post<ServiceTemplate>('/services/templates', data).then(r => r.data);

export const deleteServiceTemplate = (id: number) =>
  api.delete(`/services/templates/${id}`);

// Groups
export const getGroups = () =>
  api.get<Group[]>('/groups').then(r => r.data);

export const createGroup = (data: Partial<Group> & { service_ids?: number[] }) =>
  api.post<Group>('/groups', data).then(r => r.data);

export const updateGroup = (id: number, data: Partial<Group> & { service_ids?: number[] }) =>
  api.put<Group>(`/groups/${id}`, data).then(r => r.data);

export const deleteGroup = (id: number) =>
  api.delete(`/groups/${id}`);

// Notification channels
export const getNotificationTypes = () =>
  api.get<NotificationType[]>('/notifications/types').then(r => r.data);

export const getNotifications = () =>
  api.get<NotificationChannel[]>('/notifications').then(r => r.data);

export const getNotification = (id: number) =>
  api.get<NotificationChannel>(`/notifications/${id}`).then(r => r.data);

export const createNotification = (data: NotificationChannelInput) =>
  api.post<NotificationChannel>('/notifications', data).then(r => r.data);

export const updateNotification = (id: number, data: NotificationChannelInput) =>
  api.put<NotificationChannel>(`/notifications/${id}`, data).then(r => r.data);

export const deleteNotification = (id: number) =>
  api.delete(`/notifications/${id}`);

/** Send a sample through a saved channel. */
export const testNotification = (id: number, event = 'test') =>
  api.post<NotificationTestResult>(`/notifications/${id}/test`, { event }).then(r => r.data);

/** Try a configuration before it is saved, so a new channel can be verified from the form. */
export const testNotificationConfig = (data: { type: string; config: Record<string, unknown>; event?: string }) =>
  api.post<NotificationTestResult>('/notifications/test-config', data).then(r => r.data);

export const getNotificationDeliveries = (id: number) =>
  api.get<NotificationDelivery[]>(`/notifications/${id}/deliveries`).then(r => r.data);

/** Send a past notification again; returns the new delivery row. */
export const redeliverNotification = (id: number, deliveryId: number) =>
  api.post<NotificationDelivery>(`/notifications/${id}/deliveries/${deliveryId}/redeliver`).then(r => r.data);

// Maintenance windows
export const getMaintenanceWindows = () =>
  api.get<MaintenanceWindow[]>('/maintenance').then(r => r.data);

export const createMaintenanceWindow = (data: MaintenanceWindowInput) =>
  api.post<MaintenanceWindow>('/maintenance', data).then(r => r.data);

export const updateMaintenanceWindow = (id: number, data: MaintenanceWindowInput) =>
  api.put<MaintenanceWindow>(`/maintenance/${id}`, data).then(r => r.data);

export const deleteMaintenanceWindow = (id: number) =>
  api.delete(`/maintenance/${id}`);

// Config
export const exportConfig = (includeSecrets = false) =>
  api.get<ConfigExport>('/config/export', { params: includeSecrets ? { include_secrets: 'true' } : {} }).then(r => r.data);

/** `dryRun` previews what an import would do without writing anything. */
export const importConfig = (data: ConfigExport, dryRun = false) =>
  api.post<ImportResult>('/config/import', data, { params: dryRun ? { dry_run: 'true' } : {} }).then(r => r.data);

// Backups, retention, config-as-code
export const getBackupStatus = () =>
  api.get<BackupStatus>('/config/backups').then(r => r.data);

export const runBackup = () =>
  api.post<BackupResult>('/config/backups').then(r => r.data);

export const getRetentionPolicy = () =>
  api.get<RetentionPolicy>('/config/retention').then(r => r.data);

export const getConfigFileState = () =>
  api.get<ConfigFileState>('/config/file').then(r => r.data);

export const planConfigFile = () =>
  api.post<ImportResult>('/config/plan').then(r => r.data);

export const applyConfigFile = () =>
  api.post<ImportResult>('/config/file/apply').then(r => r.data);

// API keys (session only — a key cannot manage keys)
export const getApiKeys = (opts: { all?: boolean; includeRevoked?: boolean } = {}) =>
  api.get<ApiKey[]>('/api-keys', { params: { ...(opts.all ? { all: 'true' } : {}), ...(opts.includeRevoked ? { include_revoked: 'true' } : {}) } }).then(r => r.data);

/** The token is in the response once and never again. */
export const createApiKey = (data: ApiKeyInput) =>
  api.post<ApiKeyCreated>('/api-keys', data).then(r => r.data);

export const revokeApiKey = (id: number) =>
  api.delete(`/api-keys/${id}`);

// Browser push
export const getVapidPublicKey = () =>
  api.get<{ enabled: boolean; public_key: string }>('/browser-push/vapid-public-key').then(r => r.data);

export const getPushSubscriptions = () =>
  api.get<PushSubscriptionInfo[]>('/browser-push/subscriptions').then(r => r.data);

export const subscribeBrowserPush = (subscription: PushSubscriptionJSON) =>
  api.post<PushSubscriptionInfo>('/browser-push/subscriptions', subscription).then(r => r.data);

export const unsubscribeBrowserPush = (endpoint: string) =>
  api.delete('/browser-push/subscriptions', { data: { endpoint } });

export const testBrowserPush = () =>
  api.post<{ sent: number; failed: number }>('/browser-push/test').then(r => r.data);

// Health
export const getHealth = () =>
  api.get('/health').then(r => r.data);

// Incidents
export const getIncidents = (params?: { limit?: number; days?: number; service_id?: number }) =>
  api.get<Incident[]>('/incidents', { params }).then(r => r.data);

export const getIncidentSummary = (days = 30) =>
  api.get<IncidentSummary>('/incidents/summary', { params: { days } }).then(r => r.data);

export const resolveIncident = (id: number) =>
  api.post<Incident>(`/incidents/${id}/resolve`).then(r => r.data);

export const setIncidentPublic = (id: number, data: { public?: boolean; public_title?: string | null }) =>
  api.patch<Incident>(`/incidents/${id}`, data).then(r => r.data);

export const getIncidentUpdates = (id: number) =>
  api.get<StatusPost[]>(`/incidents/${id}/updates`).then(r => r.data);

export const postIncidentUpdate = (id: number, data: { status?: string | null; title: string; body?: string }) =>
  api.post<StatusPost>(`/incidents/${id}/updates`, data).then(r => r.data);

export const deleteIncidentUpdate = (id: number, postId: number) =>
  api.delete(`/incidents/${id}/updates/${postId}`);

// Status pages
export const getStatusPages = () =>
  api.get<{ enabled: boolean; pages: StatusPage[] }>('/status-pages').then(r => r.data);

export const getStatusPage = (id: number) =>
  api.get<StatusPageDetail>(`/status-pages/${id}`).then(r => r.data);

export const previewStatusPage = (id: number) =>
  api.get<PublicStatus>(`/status-pages/${id}/preview`).then(r => r.data);

export const createStatusPage = (data: StatusPageInput & { slug: string; title: string }) =>
  api.post<StatusPageDetail>('/status-pages', data).then(r => r.data);

export const updateStatusPage = (id: number, data: StatusPageInput) =>
  api.put<StatusPageDetail>(`/status-pages/${id}`, data).then(r => r.data);

export const deleteStatusPage = (id: number) =>
  api.delete(`/status-pages/${id}`);

export const setStatusPageLayout = (id: number, data: StatusPageLayoutInput) =>
  api.put<StatusPageDetail>(`/status-pages/${id}/layout`, data).then(r => r.data);

export const getAnnouncements = (pageId: number) =>
  api.get<StatusPost[]>(`/status-pages/${pageId}/announcements`).then(r => r.data);

export const createAnnouncement = (pageId: number, data: { title: string; body?: string; published?: boolean; starts_at?: string | null; ends_at?: string | null }) =>
  api.post<StatusPost>(`/status-pages/${pageId}/announcements`, data).then(r => r.data);

export const updateAnnouncement = (pageId: number, postId: number, data: Partial<{ title: string; body: string; published: boolean; starts_at: string | null; ends_at: string | null }>) =>
  api.put<StatusPost>(`/status-pages/${pageId}/announcements/${postId}`, data).then(r => r.data);

export const deleteAnnouncement = (pageId: number, postId: number) =>
  api.delete(`/status-pages/${pageId}/announcements/${postId}`);

// Instance settings
export const getSettings = () =>
  api.get<InstanceSettings>('/settings').then(r => r.data);

export const updateSettings = (data: Partial<InstanceSettings>) =>
  api.put<InstanceSettings>('/settings', data).then(r => r.data);

export const acknowledgeIncident = (id: number, note?: string) =>
  api.post<Incident>(`/incidents/${id}/acknowledge`, note ? { note } : {}).then(r => r.data);

// Reports
export interface ReportParams {
  scope?: string;
  from?: string;
  to?: string;
  compare?: 'previous';
}

export const getFleetReport = (params: ReportParams) =>
  api.get<FleetReport>('/reports/fleet', { params }).then(r => r.data);

export const getAvailabilityReport = (params: ReportParams) =>
  api.get<FleetReport>('/reports/availability', { params }).then(r => r.data);

export const getIncidentRateReport = (params: ReportParams) =>
  api.get<FleetReport>('/reports/incidents', { params }).then(r => r.data);

export const getResponseReport = (params: ReportParams) =>
  api.get<FleetReport & { hour_of_day: HourOfDayRow[] }>('/reports/response', { params })
    .then(r => r.data);

export const getScorecard = (id: number, params: Omit<ReportParams, 'scope'>) =>
  api.get<Scorecard>(`/reports/services/${id}`, { params }).then(r => r.data);

/** Plain href for downloads — cookie auth travels with the request, so no blob. */
export const reportCsvUrl = (report: 'fleet' | 'availability' | 'incidents' | 'response', params: ReportParams) => {
  const q = new URLSearchParams({ ...params, format: 'csv' } as Record<string, string>);
  return `/api/v1/reports/${report}?${q.toString()}`;
};
